import React, { createContext, useState, useContext } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import darkTheme from './theme';
import lightTheme from './lightTheme';

interface AnimationStage {
  opacity: number;
  y?: number;
  scale?: number;
}

interface AnimationSettings {
  initial: AnimationStage;
  animate: AnimationStage;
  exit: AnimationStage;
  transition: {
    duration?: number;
    type?: string;
    stiffness?: number;
    damping?: number;
  };
}

interface AnimationContextType {
  settings: AnimationSettings;
  updateSettings: (newSettings: Partial<AnimationSettings>) => void;
}

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

interface CombinedContextType extends AnimationContextType, ThemeContextType {}

const defaultSettings: AnimationSettings = {
  initial: { opacity: 0, y: 20, scale: 0.8 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.8 },
  transition: { duration: 0.3 },
};

const CombinedContext = createContext<CombinedContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
  isDarkMode: true,
  toggleTheme: () => {},
});

export const useCombinedContext = () => useContext(CombinedContext);

export const CombinedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AnimationSettings>(defaultSettings);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const updateSettings = (newSettings: Partial<AnimationSettings>) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      ...newSettings,
      initial: { ...prevSettings.initial, ...newSettings.initial },
      animate: { ...prevSettings.animate, ...newSettings.animate },
      exit: { ...prevSettings.exit, ...newSettings.exit },
      transition: { ...prevSettings.transition, ...newSettings.transition },
    }));
  };

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  return (
    <CombinedContext.Provider value={{ settings, updateSettings, isDarkMode, toggleTheme }}>
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        {children}
      </ThemeProvider>
    </CombinedContext.Provider>
  );
};